import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UploadApi } from './UploadApi';
import { UploadUtils } from './UploadUtils';
import { useUploadStore } from './uploadStore';
import { UPLOAD_QUERY_KEYS } from '@/lib/constants/upload';
import type { DashboardUploaderS3Cfg } from '@/lib/types/Upload';

/**
 * Hook: fetches STS credentials via useQuery and syncs to Zustand store.
 *
 * No refetchInterval — this hook is essentially dormant when idle.
 * Credential refresh is driven by ensureValidCredentials() calls inside
 * Uppy callbacks. TanStack Query here is only for the initial fetch +
 * React lifecycle integration.
 */
export function useUploadCredentials(cId: number | null) {
  const setCredentials = useUploadStore((s) => s.setCredentials);

  const query = useQuery({
    queryKey: UPLOAD_QUERY_KEYS.credentials(cId!),
    queryFn: () => UploadApi.getDashboardUploaderCfg(cId!),
    enabled: cId != null,
    staleTime: 30 * 60 * 1000, // 30 min
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Sync fetched credentials to Zustand store
  useEffect(() => {
    if (query.data?.s3Cfg && cId != null) {
      setCredentials(cId, query.data.s3Cfg);
    }
  }, [query.data, cId, setCredentials]);

  return query;
}

/**
 * Standalone async function for use inside Uppy's async callbacks
 * (which run outside React lifecycle).
 *
 * Reads credentials from Zustand store, checks validity, and refreshes
 * if needed. Uses singleton promise guard to deduplicate concurrent calls
 * (e.g., multiple files added simultaneously).
 *
 * IMPORTANT: Compares cId against credentialsCId to prevent using
 * Customer A's credentials for Customer B's uploads during switch race.
 */
let refreshPromise: Promise<DashboardUploaderS3Cfg> | null = null;

export async function ensureValidCredentials(
  cId: number
): Promise<DashboardUploaderS3Cfg> {
  const { credentials: current, credentialsCId } = useUploadStore.getState();

  if (current && credentialsCId === cId && UploadUtils.areCredentialsValid(current)) {
    return current;
  }

  // Singleton promise guard: prevents duplicate API calls when multiple
  // files trigger ensureValidCredentials() near-simultaneously.
  if (!refreshPromise) {
    refreshPromise = UploadApi.getDashboardUploaderCfg(cId)
      .then((cfg) => {
        if (cfg.s3Cfg) {
          useUploadStore.getState().setCredentials(cId, cfg.s3Cfg);
          return cfg.s3Cfg;
        }
        throw new Error('No S3 config returned from backend');
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}
